import { StatusGroup } from "@/destinations/Traces/StatusGroup";
import statusMetadata from "@/destinations/Traces/statusMetadata";
import { IngestTraceRow } from "@/destinations/types";
import { EnableDestinationContext } from "./types";

export type DeliveryOutcome =
  | "delivered"
  | "retrying"
  | "notSent"
  | "warning"
  | "failed";

// Freshpaint chose not to send these. They sit in the success group because
// nothing went wrong, but the destination never received the event.
const NOT_SENT_STATUSES = new Set([
  "consent_not_granted",
  "transformation_skip",
  "dynamic_property_skip",
  "delayed_processing",
]);

export function classifyTrace(
  trace: Pick<IngestTraceRow, "Status" | "IsProvisionalStatus">,
): DeliveryOutcome {
  if (trace.Status === "ok") {
    return "delivered";
  }
  if (trace.IsProvisionalStatus) {
    return "retrying";
  }
  if (NOT_SENT_STATUSES.has(trace.Status)) {
    return "notSent";
  }
  if (
    trace.Status === "downstream_warning" ||
    statusMetadata.get(trace.Status)?.group === StatusGroup.Warning
  ) {
    return "warning";
  }
  return "failed";
}

export type DeliverySummary = Record<
  Exclude<DeliveryOutcome, "notSent">,
  boolean
> & {
  /** Status labels explaining why events weren't sent. */
  notSentReasons: string[];
};

/**
 * Traces are sampled, so this reports which outcomes occurred rather than
 * how many events had each one.
 */
export function summarizeDeliveries(
  traces: Pick<IngestTraceRow, "Status" | "IsProvisionalStatus">[],
): DeliverySummary {
  const outcomes = new Set(traces.map(classifyTrace));
  const notSentReasons = new Set(
    traces
      .filter((trace) => classifyTrace(trace) === "notSent")
      .map((trace) => statusMetadata.get(trace.Status)?.label ?? trace.Status),
  );
  return {
    delivered: outcomes.has("delivered"),
    retrying: outcomes.has("retrying"),
    warning: outcomes.has("warning"),
    failed: outcomes.has("failed"),
    notSentReasons: [...notSentReasons],
  };
}

const MAX_WINDOW_SECONDS = 60 * 60;

/**
 * Returns the Unix time, in seconds, to start looking for traces. That is the
 * later of when the destination was last turned on and when it got new
 * credentials, so traces from earlier sessions or old credentials don't count.
 * The window never reaches back more than an hour.
 * Traces are stored truncated to the minute. Turn-on rounds down so its first
 * deliveries count. A credential save rounds up, because deliveries made with
 * the old credentials earlier in that minute share its timestamp. A save before
 * the turn-on is ignored: the destination was off, so nothing used the old
 * credentials, and rounding it up would drop the turn-on minute.
 */
export function getDeliveryWindowStart(
  lastToggledAt: string | null | undefined,
  nowSeconds: number,
  credentialsSavedAtMs?: number,
): number {
  const starts = [nowSeconds - MAX_WINDOW_SECONDS];
  const toggledAtMs = lastToggledAt ? new Date(lastToggledAt).getTime() : NaN;
  if (!Number.isNaN(toggledAtMs)) {
    starts.push(Math.floor(toggledAtMs / 60_000) * 60);
  }
  if (
    credentialsSavedAtMs !== undefined &&
    (Number.isNaN(toggledAtMs) || credentialsSavedAtMs >= toggledAtMs)
  ) {
    starts.push(Math.ceil(credentialsSavedAtMs / 60_000) * 60);
  }
  return Math.max(...starts);
}

/**
 * Only server-side deliveries are traced. A client-mode config sends browser
 * events straight from the SDK, so there would be nothing to wait for.
 */
export function isDeliveryStepApplicable({
  saasDestination,
  configuredDestination,
  isHipaaV2,
}: Pick<
  EnableDestinationContext,
  "saasDestination" | "configuredDestination" | "isHipaaV2"
>): boolean {
  if (!saasDestination) {
    return true;
  }
  if (!saasDestination.supportsServerSide) {
    return false;
  }
  // New configs are saved server-side in HIPAA mode, and in the
  // destination's default mode otherwise.
  const connectionMode =
    configuredDestination?.connection_mode ??
    (isHipaaV2 ? "server" : saasDestination.connectionMode);
  return connectionMode === "server";
}
